import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { isLinkableTin } from '../lib/tin';
import { useLocale, useT } from '../i18n/locale-context';
import { getSimilarWinners, type SimilarWinner } from '../lib/catalog-client';
import { formatBudgetCompact, formatPct, pluralKeyIndex, withCurrency } from '../lib/format';
import { useHydrated } from '../lib/use-hydrated';
import { LocaleLink } from '../i18n/locale-link';
import { AnalyticsTeaser } from './analytics-teaser';
import { SimilarWinnersTeaserBody } from './analytics-teaser-bodies';
import { InfoHint } from './info-hint';
import { splitOrgForm } from './buyer-graph-panel';
import { CompanyListSheet, KindChip, LastAwardDate } from './company-list-sheet';

/**
 * «Вероятные участники» (until 2026-10-18 «Кто выигрывает такие лоты») — the
 * companies that got contracts with the same ЕНКТ code over the last 12 months
 * (exact code first, the 6-digit class when the code alone has fewer than
 * three companies). Pro-only: the API answers a guest or a free reader with the
 * LOCKED payload — two counts and, since 2026-10-18 (owner), the REAL names of
 * the first three companies, never a number about one — and the card then
 * renders as a teaser on the `similarWinners` wall: the names in plain text
 * (no link — a tap anywhere opens /plans), the figures blurred, «Доли, даты и
 * весь список — в Pro» under them.
 *
 * Asked for by a Pro client who compared our card with TenderZone's «Возможные
 * участники» (2026-10-09). Owner, 2026-10-18, because it was read as a second
 * concentration card: renamed, subtitled «вероятно будут участвовать —
 * получали такие контракты за последний год», shown from ONE company, ordered
 * by the newest contract only, each company marked «торги» / «напрямую», and
 * «Показать всех (N)» opens the full list in a sheet over the page
 * (`CompanyListSheet`). Same fetch/gating/hide-when-empty contract as
 * `BuyerGraphPanel`: nothing renders when there is no history behind the wall.
 * No analytics event on the full list — none is in the events registry, on
 * purpose.
 */

const PLURAL_FORMS = ['one', 'few', 'many'] as const;

export function SimilarWinnersPanel({ tenderId }: { tenderId: string }) {
  const t = useT();
  const { locale } = useLocale();
  const hydrated = useHydrated();
  const [sheetOpen, setSheetOpen] = useState(false);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['similar-winners', tenderId],
    queryFn: () => getSimilarWinners(tenderId),
    // ⚠️ BROWSER ONLY, like the other analytics cards: the prerender renders
    // ~5k lot pages × 2 locales and must not fan this query out.
    enabled: hydrated,
  });

  // «Показать всех (N)» — asked only once the reader opens the sheet. The
  // server cuts it from the same cached aggregate, so it costs no second
  // computation.
  const full = useQuery({
    queryKey: ['similar-winners', tenderId, 'all'],
    queryFn: () => getSimilarWinners(tenderId, { all: true }),
    enabled: hydrated && sheetOpen,
  });

  const winsLabel = (n: number) =>
    t(`similarWinners.wins.${PLURAL_FORMS[pluralKeyIndex(n)]}`, { n });
  const companiesLabel = (n: number) =>
    t(`similarWinners.companies.${PLURAL_FORMS[pluralKeyIndex(n)]}`, { n });

  if (isLoading || isError || !data || !data.hasData) return null;

  if (data.locked) {
    return (
      <AnalyticsTeaser
        wall="similarWinners"
        tenderId={tenderId}
        title={t('similarWinners.title')}
        hint={t('similarWinners.hint')}
        note={
          data.awards > 0
            ? t('similarWinners.teaserBasis', {
                awards: winsLabel(data.awards),
                winners: companiesLabel(data.winnerCount),
              })
            : undefined
        }
      >
        {/* No more rows than winners, on every country: since the card shows
            from ONE company (2026-10-18), five rows over one winner promised a
            list Pro then does not show. */}
        <SimilarWinnersTeaserBody
          winners={data.winnerCount}
          names={data.teaser?.map((c) => c.name)}
        />
      </AnalyticsTeaser>
    );
  }

  const d = data.result;
  if (!d || d.winners.length === 0) return null;

  // The basis («код/класс ЕНКТ …, N контрактов, M компаний») lives in the hint,
  // not in a footer (Igor, 2026-10-09 — «компактно, без лишних заголовков»).
  const basis =
    d.tier === 'exact'
      ? t('similarWinners.basisExact', {
          code: d.code,
          awards: winsLabel(d.awards),
          winners: companiesLabel(d.winnerCount),
        })
      : t('similarWinners.basisClass', {
          code: d.classCode ?? d.code,
          awards: winsLabel(d.awards),
          winners: companiesLabel(d.winnerCount),
        });

  const fullData = full.data && !full.data.locked ? full.data.result : null;

  return (
    <section
      data-testid="similar-winners"
      className="rounded-lg border border-edge bg-bone-surface px-4 py-3.5 shadow-m-sm sm:px-5"
    >
      <div className="flex items-center gap-1.5">
        <h3 className="text-[14px] font-bold text-ink">{t('similarWinners.title')}</h3>
        <InfoHint text={`${t('similarWinners.hint')} ${basis}.`} />
      </div>
      <p className="mb-1.5 text-[12px] text-ink-muted">{t('similarWinners.subtitle')}</p>
      <ol className="flex flex-col">
        {d.winners.map((w, i) => (
          <WinnerRow key={w.tin} rank={i + 1} winner={w} winsLabel={winsLabel} locale={locale} />
        ))}
      </ol>

      {d.winnerCount > d.winners.length && (
        <>
          <button
            type="button"
            aria-haspopup="dialog"
            onClick={() => setSheetOpen(true)}
            className="mt-2 text-[12.5px] font-semibold text-brand hover:underline"
          >
            {t('companyList.showAll', { n: d.winnerCount })}
          </button>
          <CompanyListSheet
            open={sheetOpen}
            onOpenChange={setSheetOpen}
            title={t('similarWinners.title')}
            description={t('similarWinners.subtitle')}
            total={d.winnerCount}
            error={full.isError}
            contractsLabel={winsLabel}
            rows={
              fullData
                ? fullData.winners.map((w) => ({
                    tin: w.tin,
                    name: w.name,
                    lastAwardAt: w.lastAwardAt,
                    lastAwardApprox: w.lastAwardApprox,
                    contracts: w.wins,
                    totalUzs: w.totalUzs,
                    kind: w.kind,
                  }))
                : null
            }
          />
        </>
      )}
    </section>
  );
}

/** One company: rank · name (link) · «торги»/«напрямую» on the first line;
 *  «последний 15.10.2026 · 3 контракта · 1,2 млрд сум» under it. The share
 *  rides on the count's tooltip. */
function WinnerRow({
  rank,
  winner,
  winsLabel,
  locale,
}: {
  rank: number;
  winner: SimilarWinner;
  winsLabel: (n: number) => string;
  locale: string;
}) {
  const t = useT();
  const { name, form } = splitOrgForm(winner.name ?? winner.tin);
  const nameClass = 'overflow-hidden text-ellipsis whitespace-nowrap text-[13px] font-semibold';
  const sum =
    winner.totalUzs > 0
      ? withCurrency(
          formatBudgetCompact(String(Math.round(winner.totalUzs)), locale),
          t('tender.budget.unit'),
        )
      : null;
  return (
    <li
      title={winner.name ?? winner.tin}
      className="flex gap-2 border-t border-edge-light py-1.5 first:border-t-0"
    >
      <span className="w-3.5 shrink-0 pt-px text-[11.5px] font-bold tabular-nums text-ink-muted">
        {rank}
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex min-w-0 items-center gap-1.5">
          {/* Only a 9-digit ИНН has a profile page — see isLinkableTin. */}
          {isLinkableTin(winner.tin) ? (
            <LocaleLink
              to={`/supplier/${winner.tin}`}
              className={`${nameClass} text-brand underline decoration-brand/30 underline-offset-2 transition-colors hover:decoration-brand`}
            >
              {name}
            </LocaleLink>
          ) : (
            <span className={`${nameClass} text-ink`}>{name}</span>
          )}
          {form && (
            <span className="shrink-0 rounded border border-edge-light bg-bone px-1 py-px text-[9.5px] font-bold uppercase tracking-[0.03em] text-ink-muted">
              {form}
            </span>
          )}
          {winner.kind && <KindChip kind={winner.kind} />}
        </div>
        <div className="mt-0.5 text-[11.5px] tabular-nums text-ink-muted">
          {winner.lastAwardAt && (
            <>
              {t('companyList.last')}{' '}
              <LastAwardDate iso={winner.lastAwardAt} approx={winner.lastAwardApprox} />
              {' · '}
            </>
          )}
          <span
            className="font-semibold text-ink"
            title={`${t('similarWinners.share')}: ${formatPct(winner.sharePct)}`}
          >
            {winsLabel(winner.wins)}
          </span>
          {sum && ` · ${sum}`}
        </div>
      </div>
    </li>
  );
}
